import { TypeIdSchema } from '@unipile/db';
import { Static, Type } from '@sinclair/typebox';
import { TypeCompiler } from '@sinclair/typebox/compiler';
import { PublicAccountSchema } from '../core-api/account/account.schema';
import { StringEnum } from '../utils/typebox';
import { ProviderAuthenticationPayloadSchema } from './provider-authorization.schema';

export const CreateAuthLinkResponseSchema = Type.Object({
  object: Type.Literal('HostedAuthLink'),
  link: Type.String({
    description:
      'Link to the Hosted Auth session. Redirect your users to this link so they can authenticate their account. If you use a white-label domain, only replace the hostname with a domain that has been explicitly verified by Unipile for the parent Application.',
  }),
});
export const OauthUrlResponseSchema = Type.Object(
  {
    object: Type.Literal('OauthUrl'),
    url: Type.String({ description: 'The URL to redirect to start the oauth flow of the provider.' }),
  },
  {
    title: 'Oauth URL',
    description: 'For Oauth providers (Outlook, Google)',
  },
);

export const OauthUrlResponseValidator = TypeCompiler.Compile(OauthUrlResponseSchema);

const CaptchaCheckpointSchema = Type.Object(
  {
    type: Type.Literal('CAPTCHA'),
    public_key: Type.Union([Type.String(), Type.Null()], {
      description: 'The public key of the CAPTCHA. This is used to verify the CAPTCHA.',
    }),
    data: Type.Union([Type.String(), Type.Null()], {
      description: 'The data of the CAPTCHA. This is used to verify the CAPTCHA.',
    }),
  },
  {
    title: 'Captcha',
    description: 'The user needs to solve a captcha checkpoint.',
  },
);

const TwoFactorAuthCheckpointSchema = Type.Object(
  {
    type: Type.Literal('2FA'),
    platform: StringEnum(['SMS', 'APP', 'WHATSAPP'], {
      description: `The platform of the 2FA.
          - \`SMS\` means the code was sent in a SMS.
          - \`APP\` means the user must enter the code generated by the Authenticator App used to setup 2FA.
          - \`WHATSAPP\` means the code was sent via WhatsApp.`,
    }),
  },
  {
    title: 'Two Factor Authentication',
    description: 'The user needs to enter the code generated by the Authenticator App used to setup 2FA or received by SMS.',
  },
);

const OtpCheckpointSchema = Type.Object(
  {
    type: Type.Literal('OTP'),
    channel: Type.Optional(
      StringEnum(['EMAIL', 'SMS', 'WHATSAPP'], {
        description: 'The channel the one-time password was sent through, when known.',
      }),
    ),
  },
  {
    title: 'One-Time Password',
    description:
      "A one-time password was sent to the user's phone or email address. The user needs to enter the code in the next checkpoint.",
  },
);

const InAppValidationCheckpointSchema = Type.Object(
  {
    type: Type.Literal('IN_APP_VALIDATION'),
    code: Type.Optional(
      Type.String({
        description:
          'For number-matching approvals: the digits shown here that the user must select among the choices in their notification.',
      }),
    ),
  },
  {
    title: 'In App Validation',
    description:
      "A notification was sent to the user's phone. The user needs to validate the notification by clicking on the link in the notification.",
  },
);

const PairingCodeCheckpointSchema = Type.Object(
  {
    type: Type.Literal('PAIRING_CODE'),
    code: Type.String({ description: 'The pairing code to enter on the phone to link the device.' }),
  },
  {
    title: 'Pairing Code',
    description: 'A pairing code must be entered in the provider app to complete authentication.',
  },
);

const LoadingCheckpointSchema = Type.Object(
  {
    type: Type.Literal('LOADING'),
  },
  {
    title: 'Loading',
    description: 'The authentication is continuing in the background and requires no user input.',
  },
);

const PhoneRegisterCheckpointSchema = Type.Object(
  {
    type: Type.Literal('PHONE_REGISTER'),
  },
  {
    title: 'Phone Register',
    description: 'The user needs to register their phone number so an OTP can be sent.',
  },
);

const OTPOrInAppValidationCheckpointSchema = Type.Object(
  {
    type: Type.Literal('OTP_OR_IN_APP_VALIDATION'),
    channel: Type.Optional(
      StringEnum(['EMAIL', 'SMS', 'WHATSAPP'], {
        description: 'The channel the one-time password was sent through, when known.',
      }),
    ),
  },
  {
    title: 'OTP or In App Validation',
    description:
      'The user can either enter a one-time password sent to their phone/email or validate a notification sent to their phone.',
  },
);

const ContractSelectionCheckpointSchema = Type.Object(
  {
    type: Type.Literal('CONTRACT_SELECTION'),
    contracts: Type.Array(
      Type.Object({
        id: Type.String({ description: 'The ID of the contract.' }),
        name: Type.String({ description: 'The name of the contract.' }),
      }),
      { description: 'A list of contracts to choose from.' },
    ),
  },
  {
    title: 'Contract Selection',
    description: 'The user must pick a contract from a list to activate the correct features and complete authentication.',
  },
);

const ConsentScreenCheckpointSchema = Type.Object(
  {
    type: Type.Literal('CONSENT_SCREEN'),
    products: Type.Array(
      Type.Object({
        id: Type.String({ description: 'The ID of the product.' }),
        display_name: Type.String({ description: 'The display name of the product.' }),
        description: Type.Optional(Type.String({ description: 'What the product gives access to, shown under its name.' })),
        icon_url: Type.Optional(
          Type.String({
            description: "An image of the product's own (e.g. a Page's picture), shown instead of the provider icon.",
          }),
        ),
      }),
      { description: 'A list of products to choose from.' },
    ),
  },
  {
    title: 'Consent Screen',
    description: 'The user must select the products that will be activated on the account.',
  },
);

const ChallengeSelectionCheckpointSchema = Type.Object(
  {
    type: Type.Literal('CHALLENGE_SELECTION'),
    challenges: Type.Array(
      Type.Object({
        id: Type.String({
          description:
            'Identifier of the method (EMAIL, SMS, WHATSAPP or any agnostic ID). Send it back as the `challenge` field of Request Checkpoint to switch to this method.',
        }),
        label: Type.String({ description: 'Human-readable name of the method (e.g. "WhatsApp", "Text message", "Email").' }),
        description: Type.Optional(
          Type.String({ description: 'Extra detail about the method, e.g. the masked destination the code will be sent to.' }),
        ),
      }),
      { description: 'A list of alternative verification methods to choose from.' },
    ),
  },
  {
    title: 'Challenge Selection',
    description: 'The user must pick which alternative verification method to use to receive their code.',
  },
);

/**
 * Which Encrypted-Backup credential the user chose, sent back as the `code` of Solve Checkpoint — the same way a
 * contract or a consent screen is answered. `PIN` and `RECOVERY_CODE` also carry the value in `credential`.
 */
export const EncryptedHistoryOptionSchema = StringEnum(['PIN', 'RECOVERY_CODE', 'SKIP'], {
  description:
    'PIN and RECOVERY_CODE unlock the end-to-end encrypted message history; SKIP leaves it off, and the account then only reports new messages as they arrive.',
});

const EncryptedHistoryCheckpointSchema = Type.Object(
  {
    type: Type.Literal('ENCRYPTED_HISTORY'),
    options: Type.Array(
      Type.Object({
        id: EncryptedHistoryOptionSchema,
        label: Type.String({ description: 'Human-readable name of the option.' }),
        description: Type.Optional(Type.String({ description: 'Extra detail about what the option entails.' })),
        attempts_remaining: Type.Optional(
          Type.Integer({
            description:
              'How many more times this credential may be submitted. Each credential has a budget of its own, starting at 3, so running one out leaves the others open. A PIN is further capped at half of what the provider still allows, so the user can be wrong here without ever being the reason the backup locks. `0` means the option cannot be used any more; absent on options that take no credential.',
          }),
        ),
      }),
      { description: 'The ways the user can answer. Send the chosen `id` as the `code` of Solve Checkpoint.' },
    ),
    attempts_remaining: Type.Integer({
      description:
        'The most submissions any credential still has — what the user has left overall. `0` means every credential is spent and only skipping remains. The detail per credential is in `options[].attempts_remaining`; a credential the provider refuses to even try does not consume one.',
    }),
    provider_attempts_remaining: Type.Optional(
      Type.Integer({
        description:
          "For a PIN, how many attempts the provider itself still allows before it locks the backup. This budget is the provider's own and is never spent on the user's behalf: when it is unknown or too low, the PIN is refused rather than tried.",
      }),
    ),
    last_attempt: Type.Optional(
      Type.Object(
        {
          option: EncryptedHistoryOptionSchema,
          error: StringEnum(['INVALID_CREDENTIAL', 'REFUSED_TO_PROTECT_BACKUP'], {
            description:
              'INVALID_CREDENTIAL: the PIN or recovery code was wrong, and one attempt was spent. REFUSED_TO_PROTECT_BACKUP: the PIN was not even submitted, because the provider had too few attempts left to risk one — no attempt was spent, and only the recovery code can unlock the history now.',
          }),
        },
        {
          description:
            'Why the previous submission did not unlock the history. Absent the first time this checkpoint is served, so its presence is what tells the user their last answer failed.',
        },
      ),
    ),
  },
  {
    title: 'Encrypted History',
    description: 'The user must choose whether to unlock the end-to-end encrypted message history, and with which credential.',
  },
);

export const ProviderAuthenticationCheckpointDetailsSchema = Type.Object(
  {
    object: Type.Literal('AuthenticationCheckpoint'),
    checkpoint: Type.Union(
      [
        OtpCheckpointSchema,
        InAppValidationCheckpointSchema,
        PairingCodeCheckpointSchema,
        PhoneRegisterCheckpointSchema,
        TwoFactorAuthCheckpointSchema,
        CaptchaCheckpointSchema,
        OTPOrInAppValidationCheckpointSchema,
        ContractSelectionCheckpointSchema,
        ConsentScreenCheckpointSchema,
        ChallengeSelectionCheckpointSchema,
        LoadingCheckpointSchema,
        EncryptedHistoryCheckpointSchema,
      ],
      {
        description: 'The details of the checkpoint that user needs to solve.',
      },
    ),
    intent_id: TypeIdSchema('account', { description: 'The ID of the auth intent. This should be used in Solve Checkpoint.' }),
  },
  {
    title: 'Checkpoint',
    description: 'The authentication requires a checkpoint to be solved.',
  },
);

export const ProviderAuthenticationQrCodeSchema = Type.Object(
  {
    object: Type.Literal('AuthenticationQrCode'),
    qrcode: Type.String({ description: 'The QR Code to scan.' }),
    intent_id: TypeIdSchema('account', {
      description: 'The ID of the auth intent. This should be used to listen for the QR Code scan with Solve Checkpoint.',
    }),
  },
  {
    title: 'QR Code',
    description: 'The authentication was started for a QR Code.',
  },
);

/**
 * Response of the gateway
 */
export const ProviderAuthenticationIntentResponseSchema = Type.Union([
  ProviderAuthenticationCheckpointDetailsSchema,
  ProviderAuthenticationQrCodeSchema,
  OauthUrlResponseSchema,
  PublicAccountSchema,
]);

export const ProviderAuthenticationRequestCheckpointResponseSchema = Type.Union([
  ProviderAuthenticationCheckpointDetailsSchema,
  ProviderAuthenticationQrCodeSchema,
]);

export const ProviderAuthenticationRequestCheckpointResponseValidator = TypeCompiler.Compile(
  ProviderAuthenticationRequestCheckpointResponseSchema,
);

export const ProviderAuthenticationResendCheckpointResponseSchema = Type.Null();

/**
 * Response of the auth app
 */
export const ProviderAuthenticationResponseShapeSchema = Type.Union([
  ProviderAuthenticationPayloadSchema,
  ProviderAuthenticationCheckpointDetailsSchema,
  ProviderAuthenticationQrCodeSchema,
]);

export const ProviderAuthenticationResponseShapeValidator = TypeCompiler.Compile(ProviderAuthenticationResponseShapeSchema);

export type CaptchaCheckpoint = Static<typeof CaptchaCheckpointSchema>;
export type ContractSelectionCheckpoint = Static<typeof ContractSelectionCheckpointSchema>;
export type ConsentScreenCheckpoint = Static<typeof ConsentScreenCheckpointSchema>;
export type ChallengeSelectionCheckpoint = Static<typeof ChallengeSelectionCheckpointSchema>;
export type EncryptedHistoryCheckpoint = Static<typeof EncryptedHistoryCheckpointSchema>;
export type EncryptedHistoryOption = Static<typeof EncryptedHistoryOptionSchema>;
export type TwoFactorAuthCheckpoint = Static<typeof TwoFactorAuthCheckpointSchema>;
export type ProviderAuthenticationCheckpointDetails = Static<typeof ProviderAuthenticationCheckpointDetailsSchema>;
export type OauthUrlResponse = Static<typeof OauthUrlResponseSchema>;
export type CreateAuthLinkResponse = Static<typeof CreateAuthLinkResponseSchema>;
export type ProviderAuthenticationQrCode = Static<typeof ProviderAuthenticationQrCodeSchema>;
export type ProviderAuthenticationIntentResponse = Static<typeof ProviderAuthenticationIntentResponseSchema>;
export type ProviderAuthenticationRequestCheckpointResponse = Static<
  typeof ProviderAuthenticationRequestCheckpointResponseSchema
>;
export type ProviderAuthenticationResponseShape = Static<typeof ProviderAuthenticationResponseShapeSchema>;
